import React, { useEffect, useState } from 'react';
import { lineAPI, cityAPI, stationAPI, lineRouteAPI, routePathAPI } from '../services/api';
import { Line, City, Station, LineStationInfo, LineRoute, Direction } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Divider,
  Checkbox,
  Badge,
  Autocomplete
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Refresh, 
  Add, 
  Route,
  Train,
  LocationOn,
  Search,
  DragIndicator,
  Remove,
  ExpandMore
} from '@mui/icons-material';
import TranslationInput from '../components/TranslationInput';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Lines: React.FC = () => {
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    cityGroup: '',
    color: '#1976d2',
    isActive: true,
  });
  const [routes, setRoutes] = useState<LineRoute[]>([]);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [generatingPathId, setGeneratingPathId] = useState<string | null>(null);
  const [generatingRoutePathsId, setGeneratingRoutePathsId] = useState<string | null>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [fetchingCities, setFetchingCities] = useState(false);
  const [fetchingStations, setFetchingStations] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cityChangeWarning, setCityChangeWarning] = useState('');
  const [selectedLine, setSelectedLine] = useState<Line | null>(null);
  const [selectedLineLoading, setSelectedLineLoading] = useState(false);
  
  // City filter state
  const [selectedCity, setSelectedCity] = useState<string>('');

  useEffect(() => {
    // Only fetch cities initially, not lines
    fetchCities();
  }, []);

  const fetchLines = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await lineAPI.getAll();
      setLines(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load lines');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLinesByCity = async (cityId: string) => {
    if (!cityId) {
      setLines([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const res = await lineAPI.getByCity(cityId);
      setLines(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load lines for city');
      setLines([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    setFetchingCities(true);
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load cities');
    } finally {
      setFetchingCities(false);
    }
  };

  const fetchStationsByCity = async (cityId: string) => {
    setFetchingStations(true);
    try {
      if (cityId) {
        const res = await stationAPI.getByCity(cityId);
        setStations(res.data);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to fetch stations for city ID:', cityId, error);
    } finally {
      setFetchingStations(false);
    }
  };

  const fetchLineById = async (lineId: string) => {
    setSelectedLineLoading(true);
    setError(null);
    try {
      const res = await lineAPI.getById(lineId);
      setSelectedLine(res.data);
      console.log('Fetched line details:', res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch line details';
      setError(errorMessage);
      console.error('Error fetching line details:', error);
    } finally {
      setSelectedLineLoading(false);
    }
  };

  // Helper function to get city name from city ID
  const getCityName = (cityId: string): string => {
    const city = cities.find(c => c.id === cityId);
    return city ? city.name : cityId; // Fallback to ID if city not found
  };

  const openAddModal = async () => {
    setIsEditMode(false);
    setEditingLineId(null);
    setShowModal(true);
    setForm({ name: '', code: '', cityGroup: '', color: '#1976d2', isActive: true });
    setRoutes([]);
    setFormError('');
    await fetchCities();
  };

  const openEditModal = async (line: Line) => {
    setIsEditMode(true);
    setEditingLineId(line.id!);
    setShowModal(true);
    setForm({
      name: line.name,
      code: line.code,
      cityGroup: line.cityGroup,
      color: line.color,
      isActive: line.isActive || true,
    });
    
    // Load routes for this line
    try {
      const routesRes = await lineRouteAPI.getByLine(line.id!);
      setRoutes(routesRes.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load routes:', error);
      setRoutes([]);
    }
    
    setFormError('');
    await fetchCities();
    await fetchStationsByCity(line.cityGroup);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingLineId(null);
    setForm({ name: '', code: '', cityGroup: '', color: '#1976d2', isActive: true });
    setRoutes([]);
    setFormError('');
    setCityChangeWarning('');
    setSearchTerm('');
  };

  const handleTextFieldInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setForm({ ...form, [name]: value });
    
    // If city is selected, fetch stations for that city and clear routes
    if (name === 'cityGroup' && value) {
      fetchStationsByCity(value);
      setRoutes([]);
      setCityChangeWarning('City changed. Routes have been cleared. Please add stations for the new city.');
      setFormError('');
    } else {
      setFormError('');
    }
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, color: e.target.value });
  };

  // Route management functions
  const addRoute = () => {
    const newRoute: LineRoute = {
      routeId: `ROUTE_${Date.now()}`,
      routeName: '',
      startStation: '',
      endStation: '',
      stations: [],
      isMainRoute: routes.length === 0, // First route is main route
      direction: 'BOTH' as Direction,
      isActive: true
    };
    setRoutes([...routes, newRoute]);
    setCityChangeWarning(''); // Clear warning when route is added
  };

  const updateRoute = (index: number, field: keyof LineRoute, value: string | boolean | LineStationInfo[]) => {
    const updatedRoutes = [...routes];
    updatedRoutes[index] = { ...updatedRoutes[index], [field]: value };
    setRoutes(updatedRoutes);
  };

  const removeRoute = (index: number) => {
    const updatedRoutes = routes.filter((_, i) => i !== index);
    // If we removed the main route and there are other routes, make the first one main
    if (routes[index].isMainRoute && updatedRoutes.length > 0) {
      updatedRoutes[0].isMainRoute = true;
    }
    setRoutes(updatedRoutes);
  };

  const addStationToRoute = (routeIndex: number, stationId: string) => {
    const route = routes[routeIndex];
    
    // Check if station is already in the route
    if (route.stations.some(routeStation => routeStation.stationId === stationId)) {
      return;
    }

    const newStation: LineStationInfo = {
      stationId: stationId,
      order: route.stations.length + 1,
      reverseOrder: 1 // New station becomes the last in reverse order
    };

    const updatedRoutes = [...routes];
    const updatedStations = [...route.stations, newStation];
    
    // Update reverseOrder for all existing stations
    updatedStations.forEach((station, index) => {
      station.reverseOrder = updatedStations.length - index;
    });
    
    updatedRoutes[routeIndex].stations = updatedStations;
    
    // Auto-update start and end stations
    if (updatedRoutes[routeIndex].stations.length === 1) {
      // First station becomes start station
      updatedRoutes[routeIndex].startStation = stationId;
    }
    // Last station becomes end station
    updatedRoutes[routeIndex].endStation = stationId;
    
    setRoutes(updatedRoutes);
  };

  const removeStationFromRoute = (routeIndex: number, stationIndex: number) => {
    const updatedRoutes = [...routes];
    const route = updatedRoutes[routeIndex];
    route.stations = route.stations.filter((_, i) => i !== stationIndex);
    
    // Reorder remaining stations
    route.stations = route.stations.map((station, i) => ({
      ...station,
      order: i + 1,
      reverseOrder: route.stations.length - i
    }));
    
    // Auto-update start and end stations after removal
    if (route.stations.length > 0) {
      route.startStation = route.stations[0].stationId;
      route.endStation = route.stations[route.stations.length - 1].stationId;
    } else {
      // No stations left, clear start and end
      route.startStation = '';
      route.endStation = '';
    }
    
    setRoutes(updatedRoutes);
  };

  const moveStationInRoute = (routeIndex: number, stationIndex: number, direction: 'up' | 'down') => {
    const updatedRoutes = [...routes];
    const route = updatedRoutes[routeIndex];
    const stations = [...route.stations];
    
    if (direction === 'up' && stationIndex > 0) {
      [stations[stationIndex], stations[stationIndex - 1]] = [stations[stationIndex - 1], stations[stationIndex]];
    } else if (direction === 'down' && stationIndex < stations.length - 1) {
      [stations[stationIndex], stations[stationIndex + 1]] = [stations[stationIndex + 1], stations[stationIndex]];
    }
    
    // Update order numbers
    stations.forEach((station, i) => {
      station.order = i + 1;
      station.reverseOrder = stations.length - i;
    });
    
    route.stations = stations;
    
    // Auto-update start and end stations after reordering
    if (route.stations.length > 0) {
      route.startStation = route.stations[0].stationId;
      route.endStation = route.stations[route.stations.length - 1].stationId;
    }
    
    setRoutes(updatedRoutes);
  };

  const handleSaveLine = async () => {
    if (!form.name || !form.code || !form.cityGroup) {
      setFormError('Name, Code, and City Group are required');
      return;
    }

    setSaving(true);
    try {
      const lineData = {
        ...form,
        routes: routes.map(route => ({
          ...route,
          stations: route.stations.map(station => ({
            stationId: station.stationId,
            order: station.order,
            reverseOrder: station.reverseOrder
          }))
        }))
      };

      if (isEditMode && editingLineId) {
        await lineAPI.update(editingLineId, lineData);
        setSuccessMsg('Line updated successfully');
      } else {
        await lineAPI.create(lineData);
        setSuccessMsg('Line created successfully');
      }
      if (selectedCity) {
        await fetchLinesByCity(selectedCity);
      }
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save line');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this line?')) return;
    
    setDeletingId(id);
    try {
      await lineAPI.delete(id);
      setSuccessMsg('Line deleted successfully');
      if (selectedCity) {
        await fetchLinesByCity(selectedCity);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete line');
    } finally {
      setDeletingId(null);
    }
  };
  
  const handleGeneratePath = async (id: string) => {
    if (!window.confirm('Are you sure you want to regenerate the path for this line? This will update all station connections.')) return;
    
    setGeneratingPathId(id);
    try {
      await lineAPI.generatePath(id);
      setSuccessMsg('Path generated successfully for the line');
      if (selectedCity) {
        await fetchLinesByCity(selectedCity);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to generate path for line');
    } finally {
      setGeneratingPathId(null);
    }
  };

  const handleGenerateRoutePaths = async (id: string) => {
    if (!window.confirm('Are you sure you want to generate route paths for this line? This will delete existing route paths and create new ones from Google Maps API.')) return;
    
    setGeneratingRoutePathsId(id);
    try {
      const response = await routePathAPI.generateRoutePathsForLine(id);
      setSuccessMsg(response.data.message);
      if (selectedCity) {
        await fetchLinesByCity(selectedCity);
      }
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to generate route paths for line');
    } finally {
      setGeneratingRoutePathsId(null);
    }
  };

  const getStationName = (stationId: string) => {
    return stations.find(s => s.id === stationId)?.name || 'Unknown Station';
  };

  const getDirectionLabel = (direction: Direction) => {
    switch (direction) {
      case 'FORWARD':
        return 'Forward';
      case 'REVERSE':
        return 'Reverse';
      case 'BOTH':
        return 'Both';
      default:
        return direction;
    }
  };
  
  const handleCityChange = (cityName: string) => {
    setSelectedCity(cityName);
    if (cityName) {
      fetchLinesByCity(cityName);
    } else {
      setLines([]);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Lines Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => selectedCity ? fetchLinesByCity(selectedCity) : null}
            disabled={loading || !selectedCity}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add Line
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {/* City Filter Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" component="h2">
            Select City to View Lines
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>City</InputLabel>
            <Select
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
              label="City"
              disabled={fetchingCities}
            >
              <MenuItem value="">
                <em>Select a city to view lines</em>
              </MenuItem>
              {cities.map(city => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fetchingCities && <CircularProgress size={20} />}
          {selectedCity && (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip 
                label={`Showing lines for: ${getCityName(selectedCity)}`} 
                color="primary" 
                variant="outlined"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleCityChange('')}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                Clear
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {!selectedCity ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <LocationOn color="action" sx={{ fontSize: 80 }} />
            <Typography variant="h6" color="text.secondary">
              Select a city to view metro lines
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Choose a city from the dropdown above to see all metro lines and routes for that city
            </Typography>
          </Box>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>City Group</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Routes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!selectedCity ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={3}>
                        <Typography variant="body1" color="text.secondary">
                          Please select a city to view lines
                        </Typography>
                        <LocationOn color="action" sx={{ fontSize: 40 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={3}>
                        <Typography variant="body1" color="text.secondary">
                          No lines found for {getCityName(selectedCity)}
                        </Typography>
                        <Train color="action" sx={{ fontSize: 40 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : lines.map(line => (
                  <TableRow 
                    key={line.id} 
                    hover 
                    onClick={() => fetchLineById(line.id!)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <TableCell>{line.name}</TableCell>
                    <TableCell>{line.code}</TableCell>
                    <TableCell>{getCityName(line.cityGroup)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={line.color} 
                        size="small" 
                        sx={{ 
                          backgroundColor: line.color, 
                          color: '#fff',
                          '& .MuiChip-label': { color: '#fff' }
                        }} 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={line.isActive ? 'Active' : 'Inactive'} 
                        size="small"
                        color={line.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Chip 
                          icon={<Route />}
                          label={`${line.routes?.length || 0} routes`} 
                          size="small" 
                        />
                        {line.routes && line.routes.length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {line.routes.slice(0, 2).map((route, index) => (
                              <Chip
                                key={index}
                                label={getDirectionLabel(route.direction)}
                                size="small"
                                color={
                                  route.direction === 'BOTH' ? 'primary' : 
                                  route.direction === 'FORWARD' ? 'success' : 'warning'
                                }
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                            {line.routes.length > 2 && (
                              <Chip
                                label={`+${line.routes.length - 2}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Generate Path">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGeneratePath(line.id!);
                            }}
                            disabled={generatingPathId === line.id}
                          >
                            {generatingPathId === line.id ? <CircularProgress size={16} /> : <Route />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate Route Paths">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateRoutePaths(line.id!);
                            }}
                            disabled={generatingRoutePathsId === line.id}
                          >
                            {generatingRoutePathsId === line.id ? <CircularProgress size={16} /> : <Train />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(line);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(line.id!);
                            }}
                            disabled={deletingId === line.id}
                          >
                            {deletingId === line.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Selected Line Details */}
      {selectedLine && (
        <Paper sx={{ mt: 3, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="h2">
              Line Details: {selectedLine.name}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedLine(null)}
            >
              Close Details
            </Button>
          </Box>
          
          {selectedLineLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{selectedLine.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Code</Typography>
                <Typography variant="body1">{selectedLine.code}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">City Group</Typography>
                <Typography variant="body1">{getCityName(selectedLine.cityGroup)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={selectedLine.isActive ? 'Active' : 'Inactive'} 
                  size="small"
                  color={selectedLine.isActive ? 'success' : 'default'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Color</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: selectedLine.color,
                      borderRadius: 1,
                      border: '1px solid #ccc'
                    }}
                  />
                  <Typography variant="body2">{selectedLine.color}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Total Routes</Typography>
                <Typography variant="body1">{selectedLine.routes?.length || 0}</Typography>
              </Grid>
              
              {/* Routes Details */}
              {selectedLine.routes && selectedLine.routes.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Routes
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedLine.routes.map((route, index) => (
                      <Chip
                        key={index}
                        icon={<Route />}
                        label={`${getDirectionLabel(route.direction)} - ${route.routeName || `Route ${index + 1}`}`}
                        size="small"
                        color={
                          route.direction === 'BOTH' ? 'primary' : 
                          route.direction === 'FORWARD' ? 'success' : 'warning'
                        }
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>
      )}
      
      {/* Line Creation/Edit Modal */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Train />
            {isEditMode ? 'Edit Line' : 'Add New Line'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          {cityChangeWarning && <Alert severity="warning" sx={{ mb: 2 }}>{cityChangeWarning}</Alert>}
          
          {/* Line Basic Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>Line Information</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TranslationInput
                label="Name"
                value={form.name}
                onChange={(value) => setForm({ ...form, name: value })}
                entityType="LINE"
                entityId={isEditMode && editingLineId ? editingLineId : 'new'}
                fieldName="name"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Code" 
                name="code" 
                value={form.code} 
                onChange={handleTextFieldInput} 
                fullWidth 
                required 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>City Group</InputLabel>
                <Select
                  name="cityGroup"
                  value={form.cityGroup}
                  onChange={handleSelectInput}
                  disabled={fetchingCities}
                  label="City Group"
                >
                  <MenuItem value="">Select a city</MenuItem>
                  {cities.map(city => (
                    <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                  ))}
                </Select>
                {fetchingCities && <CircularProgress size={20} sx={{ position: 'absolute', right: 10, top: 10 }} />}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  label="Color"
                  name="color"
                  value={form.color}
                  onChange={handleTextFieldInput}
                  sx={{ flexGrow: 1 }}
                  placeholder="#1976d2"
                />
                <input
                  type="color"
                  value={form.color}
                  onChange={handleColorChange}
                  style={{ width: 50, height: 56, border: '1px solid #ccc', borderRadius: 4 }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: form.color,
                    borderRadius: 1,
                    border: '1px solid #ccc'
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="isActive" checked={form.isActive} onChange={handleCheckbox} />}
                label="Active"
              />
            </Grid>
          </Grid>

          {/* Routes Management */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Route />
              Routes ({routes.length})
            </Box>
          </Typography>
          
          {routes.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No routes defined. Add at least one route to continue.
            </Alert>
          )}

          {routes.map((route, routeIndex) => (
            <Accordion key={routeIndex} defaultExpanded sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  backgroundColor: route.isMainRoute ? 'primary.light' : 'background.paper',
                  '&:hover': { backgroundColor: route.isMainRoute ? 'primary.light' : 'action.hover' }
                }}
              >
                <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1 }}>
                  <Badge badgeContent={route.stations.length} color="primary">
                    <Route color={route.isMainRoute ? 'primary' : 'action'} />
                  </Badge>
                  <Box display="flex" flexDirection="column">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1" fontWeight={route.isMainRoute ? 600 : 500}>
                        {route.routeName || `Route ${routeIndex + 1}`}
                      </Typography>
                      <Chip 
                        label={getDirectionLabel(route.direction)} 
                        size="small" 
                        color={
                          route.direction === 'BOTH' ? 'primary' : 
                          route.direction === 'FORWARD' ? 'success' : 'warning'
                        }
                        variant="outlined"
                      />
                    </Box>
                    {route.stations.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {getStationName(route.stations[0].stationId)} → {getStationName(route.stations[route.stations.length - 1].stationId)}
                      </Typography>
                    )}
                  </Box>
                  {route.isMainRoute && (
                    <Chip label="Main Route" size="small" color="primary" variant="filled" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3 }}>
                <Grid container spacing={2}>
                  {/* Route Information */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Route Name"
                      value={route.routeName}
                      onChange={(e) => updateRoute(routeIndex, 'routeName', e.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Start Station (Auto-calculated)"
                      value={route.startStation ? getStationName(route.startStation) : 'No stations added'}
                      fullWidth
                      required
                      disabled
                      InputProps={{
                        readOnly: true,
                        sx: { 
                          backgroundColor: 'action.hover',
                          '& .MuiInputBase-input': { color: 'text.primary' }
                        }
                      }}
                      helperText="First station in the route"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="End Station (Auto-calculated)"
                      value={route.endStation ? getStationName(route.endStation) : 'No stations added'}
                      fullWidth
                      required
                      disabled
                      InputProps={{
                        readOnly: true,
                        sx: { 
                          backgroundColor: 'action.hover',
                          '& .MuiInputBase-input': { color: 'text.primary' }
                        }
                      }}
                      helperText="Last station in the route"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Direction</InputLabel>
                      <Select
                        value={route.direction}
                        onChange={(e) => updateRoute(routeIndex, 'direction', e.target.value)}
                        label="Direction"
                      >
                        <MenuItem value="BOTH">Both Directions (A ↔ B)</MenuItem>
                        <MenuItem value="FORWARD">Forward Direction (A → B)</MenuItem>
                        <MenuItem value="REVERSE">Reverse Direction (B → A)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={1} alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={route.isMainRoute}
                            onChange={(e) => {
                              // If this route becomes main, uncheck others
                              const updatedRoutes = routes.map((r, i) => ({
                                ...r,
                                isMainRoute: i === routeIndex ? e.target.checked : false
                              }));
                              setRoutes(updatedRoutes);
                            }}
                          />
                        }
                        label="Main Route"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={route.isActive}
                            onChange={(e) => updateRoute(routeIndex, 'isActive', e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>

                  {/* Stations in Route */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn />
                        Stations ({route.stations.length})
                      </Box>
                    </Typography>
                    


                    {route.stations.length > 0 && (
                      <Paper sx={{ maxHeight: 250, overflow: 'auto', mb: 1, border: '1px solid', borderColor: 'divider' }}>
                        <List dense>
                          {route.stations.map((station, stationIndex) => (
                          <React.Fragment key={station.stationId}>
                            <ListItem 
                              sx={{ 
                                backgroundColor: stationIndex % 2 === 0 ? 'background.paper' : 'action.hover',
                                '&:hover': { backgroundColor: 'action.selected' }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip 
                                  label={`${station.order}→${station.reverseOrder}`} 
                                  size="small" 
                                  color="primary" 
                                  variant="outlined"
                                  sx={{ minWidth: 50 }}
                                />
                                <DragIndicator sx={{ color: 'text.secondary' }} />
                              </Box>
                              <ListItemText 
                                primary={
                                  <Typography variant="body2" fontWeight={500}>
                                    {getStationName(station.stationId)}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    Forward: #{station.order} | Reverse: #{station.reverseOrder} | Total: {route.stations.length}
                                  </Typography>
                                }
                                sx={{ ml: 1 }}
                              />
                              <ListItemSecondaryAction>
                                <Box display="flex" gap={0.5}>
                                  <Tooltip title="Move Up">
                                    <IconButton
                                      size="small"
                                      disabled={stationIndex === 0}
                                      onClick={() => moveStationInRoute(routeIndex, stationIndex, 'up')}
                                      sx={{ 
                                        color: stationIndex === 0 ? 'text.disabled' : 'primary.main',
                                        '&:hover': { backgroundColor: 'primary.light' }
                                      }}
                                    >
                                      ↑
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Move Down">
                                    <IconButton
                                      size="small"
                                      disabled={stationIndex === route.stations.length - 1}
                                      onClick={() => moveStationInRoute(routeIndex, stationIndex, 'down')}
                                      sx={{ 
                                        color: stationIndex === route.stations.length - 1 ? 'text.disabled' : 'primary.main',
                                        '&:hover': { backgroundColor: 'primary.light' }
                                      }}
                                    >
                                      ↓
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove Station">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeStationFromRoute(routeIndex, stationIndex)}
                                      sx={{ 
                                        '&:hover': { backgroundColor: 'error.light' }
                                      }}
                                    >
                                      <Remove />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ListItemSecondaryAction>
                            </ListItem>
                            {stationIndex < route.stations.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                        </List>
                      </Paper>
                    )}

                    <Box display="flex" gap={1} alignItems="flex-end">
                      <Autocomplete
                        options={stations.filter(station => 
                          !route.stations.some(routeStation => routeStation.stationId === station.id)
                        )}
                        getOptionLabel={(option) => option.name}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            addStationToRoute(routeIndex, newValue.id!);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Add Station"
                            placeholder="Search and select station to add"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <Add sx={{ mr: 1, color: 'action.active' }} />
                                  {params.InputProps.startAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1" fontWeight={500}>
                                {option.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.stationCode} • {option.cityName}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        noOptionsText="No available stations"
                        loading={fetchingStations}
                        loadingText="Loading stations..."
                        filterOptions={(options, { inputValue }) => {
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter(option => 
                            option.name.toLowerCase().includes(searchTerm) ||
                            option.stationCode.toLowerCase().includes(searchTerm) ||
                            option.cityName?.toLowerCase().includes(searchTerm)
                          );
                        }}
                        sx={{ minWidth: 250 }}
                      />
                      <Chip 
                        label={`${route.stations.length} stations`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                  </Grid>

                  {/* Route Actions */}
                  <Grid item xs={12}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => removeRoute(routeIndex)}
                        disabled={routes.length === 1}
                      >
                        Remove Route
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={addRoute}
            sx={{ mt: 2 }}
          >
            Add Route
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveLine} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (isEditMode ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Lines; 